// Effect particles that stick to what they hit: a drop leaves a splat on any
// body it lands on, smeared along the surface when it lands fast. Characters
// take it through their own decals. Visual only.
import * as PIXI from 'pixi.js';
import { game } from '../../Game';
import { Settings } from '../../Settings';
import { flatStainTexture, neutralBloodTexture } from '../bloodTexture';
import { clearField, refreshField, segmentHit } from './surfaceField';
import { clearPaint, flushPaint, paintStamp } from './surfacePaint';

const FLESH_DECAL_MS = 120;
const BURIED_STAINS = 0.35;
const fleshDecals = new WeakMap();
let splat = null;
let flatSplat = null;

const accept = body => {
	const sprite = body.mySprite;
	if (!sprite || sprite.destroyed || sprite.isPhysicsParticleSprite || body.isPortalGhost || body.isPortalShadow) return false;
	if (sprite.data?.trigger || sprite.data?.type === game.editor.object_TRIGGER) return false;
	const display = body.myTexture || sprite;
	return display.renderable !== false && display.visible !== false;
};

export const beginSplatFrame = () => {
	if (game.world) refreshField(game.world, accept);
};

export const endSplatFrame = () => flushPaint();

export const resetSplats = () => {
	clearField();
	clearPaint();
};

// A SuperEmitter collider: called with a particle's step, returns whether it hit.
export const splatCollider = (emitter, p, sprite, fromX, fromY) => {
	const ptm = Settings.PTM;
	const hit = segmentHit(fromX / ptm, fromY / ptm, p.x / ptm, p.y / ptm);
	if (!hit) return false;
	const body = hit.body;
	const scale = p.lightweight ? sprite.scaleY : sprite.scale.y;
	const size = sprite.texture.width * scale * (1.5 + Math.random() * 1.1);
	const color = p.color ? (p.color[0] << 16) | (p.color[1] << 8) | p.color[2] : sprite.tint;
	if (body.isFlesh && body.myTexture) {
		const now = performance.now();
		if (now - (fleshDecals.get(body) ?? -Infinity) >= FLESH_DECAL_MS) {
			fleshDecals.set(body, now);
			game.editor.queueDecalToBody(body, {x: hit.x, y: hit.y}, 'Decal.png', false, Math.max(0.2, size / 31), 0, {splat: true, paintColor: emitter.ownColor ? color : undefined});
		}
		return true;
	}
	splat = splat || neutralBloodTexture(PIXI.Texture.from('Decal.png'));
	flatSplat = flatSplat || flatStainTexture(PIXI.Texture.from('Decal.png'));
	const texture = emitter.ownColor ? flatSplat : splat;
	// A drop inside the ground stays there instead of falling through to whatever is below.
	if (hit.buried) {
		if (Math.random() < BURIED_STAINS) {
			paintStamp(body, hit.x, hit.y, {texture, color, alpha: 1, size: size * 0.7, angle: Math.random() * Math.PI * 2});
		}
		return true;
	}
	const vx = p.velX;
	const vy = p.velY;
	const speed = Math.sqrt(vx * vx + vy * vy);
	const along = (vx * -hit.ny + vy * hit.nx) / (speed || 1);
	const forward = Math.sign(along || 1);
	paintStamp(body, hit.x, hit.y, {
		texture,
		color,
		alpha: emitter.paint ? 1 : 0.85 + Math.random() * 0.15,
		size,
		angle: Math.atan2(hit.nx * forward, -hit.ny * forward),
		stretch: 1 + Math.abs(along) * Math.min(2.2, speed / 500),
	});
	// Splash droplets thrown along the surface, mostly the way the drop was going.
	const metres = size / ptm;
	for (let i = 1 + Math.floor(Math.random() * 3); i > 0; i--) {
		const way = Math.random() < 0.7 ? forward : -forward;
		const reach = metres * (0.6 + Math.random() * 1.2) * way;
		const lift = metres * (Math.random() - 0.6) * 0.25;
		paintStamp(body, hit.x - hit.ny * reach + hit.nx * lift, hit.y + hit.nx * reach + hit.ny * lift, {
			texture,
			color,
			alpha: emitter.paint ? 1 : 0.75 + Math.random() * 0.25,
			size: size * (0.22 + Math.random() * 0.25),
			angle: Math.random() * Math.PI * 2,
		});
	}
	return true;
};
